import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { OverviewResponse } from '@/types/overview';
import type { DailyStats } from '@/types/feedback';
import { netSentiment, halfSplitDelta } from '@/lib/trend';
import { cn } from '@/lib/utils';
import { Sparkline } from '../charts/Sparkline';
import { StarGauge } from '../charts/StarGauge';
import { DivergingBar } from '../charts/DivergingBar';
import { RatioBar } from '../charts/RatioBar';
import { DeltaBadge } from '../DeltaBadge';

// Net sentiment (%) for a set of daily points, aggregating up/down/count.
function netForDays(days: DailyStats[]): number {
  const up = days.reduce((s, d) => s + d.thumbsUp, 0);
  const down = days.reduce((s, d) => s + d.thumbsDown, 0);
  const count = days.reduce((s, d) => s + d.count, 0);
  return netSentiment(up, down, count);
}

// First-half vs second-half move (second − first) of a per-day series reduced by
// `reduce`. Returns null when there aren't enough days to split — surfaced as a
// flat "—" delta. This is the same in-window comparison the rating delta uses;
// the portfolio overview does not fetch a prior window.
function halfDelta(days: DailyStats[], reduce: (d: DailyStats[]) => number): number | null {
  if (days.length < 2) return null;
  const mid = Math.ceil(days.length / 2);
  return reduce(days.slice(mid)) - reduce(days.slice(0, mid));
}

const DELTA_LABEL = 'in period';

/**
 * Portfolio KPI strip for the Products overview: total responses, avg rating,
 * net sentiment and active accounts across all (filtered) accounts. Each card
 * pairs its metric with a fitting visual (directional sparkline, star gauge,
 * diverging bar, ratio bar) and a consistent delta line. Deltas are the
 * in-window first-half-vs-second-half move (labelled "in period") — the overview
 * does not fetch a prior window; Active accounts has no such signal, so it is flat.
 */
export function PortfolioKpiStrip({ overview }: { overview: OverviewResponse }) {
  const daily = overview.dailyBreakdown;
  const total = overview.totalFeedback;
  const net = netSentiment(overview.votes.up, overview.votes.down, total);

  const countSeries = daily.map((d) => d.count);
  const countAvg = countSeries.length > 0 ? countSeries.reduce((s, n) => s + n, 0) / countSeries.length : undefined;

  // Directional deltas from the in-window half split.
  const countDelta = halfDelta(daily, (ds) => ds.reduce((s, d) => s + d.count, 0));
  const ratingDelta = halfSplitDelta(daily).delta;
  const netDelta = halfDelta(daily, netForDays);

  // Sparkline colour follows the responses trend: up = success, down = error.
  const countStroke =
    countDelta === null || countDelta === 0
      ? 'var(--muted-foreground)'
      : countDelta > 0
        ? 'var(--success-600)'
        : 'var(--error-600)';

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <KpiCard
        title="Total responses"
        delta={countDelta}
        deltaFormat={(n) => `${n > 0 ? '+' : ''}${Math.round(n)}`}
      >
        <div className="flex items-end justify-between gap-3">
          <div className="text-3xl font-bold leading-none">{total.toLocaleString()}</div>
          <Sparkline
            values={countSeries}
            width={96}
            height={40}
            baseline={countAvg}
            stroke={countStroke}
            aria-label="Total responses trend"
          />
        </div>
      </KpiCard>

      <KpiCard
        title="Avg rating"
        delta={ratingDelta}
        deltaFormat={(n) => `${n > 0 ? '+' : ''}${n.toFixed(1)}`}
      >
        <div>
          <div className="text-3xl font-bold leading-none">
            {overview.averageRating.toFixed(1)}
            <span className="text-lg font-semibold text-muted-foreground"> / 5</span>
          </div>
          <StarGauge value={overview.averageRating} className="mt-2.5" />
        </div>
      </KpiCard>

      <KpiCard
        title="Net sentiment"
        delta={netDelta}
        deltaFormat={(n) => `${n > 0 ? '+' : ''}${Math.round(n)} pts`}
      >
        <div className="flex h-full flex-col justify-end">
          <div className="mb-3 text-3xl font-bold leading-none">
            {net > 0 ? '+' : ''}
            {net}%
          </div>
          <DivergingBar positive={overview.votes.up} negative={overview.votes.down} />
        </div>
      </KpiCard>

      <KpiCard title="Active accounts" delta={null}>
        <div className="flex h-full flex-col justify-end">
          <div className="mb-3 text-3xl font-bold leading-none">
            {overview.activeAccounts}
            <span className="text-lg font-semibold text-muted-foreground"> / {overview.totalAccounts}</span>
          </div>
          <RatioBar
            value={overview.activeAccounts}
            total={overview.totalAccounts}
            caption="with feedback in period"
          />
        </div>
      </KpiCard>
    </div>
  );
}

function KpiCard({
  title,
  delta,
  deltaFormat,
  children,
}: {
  title: string;
  /** null → not computable (flat "—"); number → DeltaBadge (0 = "No change"). */
  delta: number | null;
  deltaFormat?: (n: number) => string;
  children: ReactNode;
}) {
  return (
    <Card className="flex min-h-[168px] flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        <div className="flex-1">{children}</div>
        {delta === null ? (
          <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
            <span aria-hidden="true">—</span>
            <span>Not available {DELTA_LABEL}</span>
          </div>        ) : (
          <DeltaBadge delta={delta} format={deltaFormat} label={DELTA_LABEL} className={cn('mt-3')} />
        )}
      </CardContent>
    </Card>
  );
}
